import { useMemo, useState } from 'react';
import { useHingesStore } from '@/state/hingesStore';
import type { HingeDef } from '@/render/hinges';
import { hingeDegFromT } from '@/render/hinges';

/**
 * Right-side panel listing every door / window hinge in the live build,
 * grouped by category. Each row is an independent slider that drives the
 * matching pivot group's rotation in the scene (see App.tsx subscription
 * that forwards values to viewport.setHingeAngle).
 *
 * Three category groups are rendered top to bottom:
 *   - Exterior doors  (front, back, side)
 *   - Windows         (every casement leaf)
 *   - Interior doors  (room-to-room)
 *
 * The whole panel is collapsible (default: open if there are <= 12 hinges,
 * collapsed otherwise — large 2-story houses can produce 25+ leaves and
 * an open list at startup is overwhelming).
 *
 * The "Open all / Close all" actions in the header act on the currently
 * visible group set, which is everything: it's almost always what you
 * want when building a quick demo of the house breathing.
 */

interface CategoryConfig {
  key: HingeDef['category'];
  title: string;
}

const CATEGORIES: CategoryConfig[] = [
  { key: 'exterior-door', title: 'Exterior Doors' },
  { key: 'window', title: 'Windows' },
  { key: 'interior-door', title: 'Interior Doors' },
  { key: 'gate', title: 'Gates' },
];

export function HingesPanel() {
  const defs = useHingesStore((s) => s.defs);
  const values = useHingesStore((s) => s.values);
  const inverted = useHingesStore((s) => s.inverted);
  const setValue = useHingesStore((s) => s.setValue);
  const setAll = useHingesStore((s) => s.setAll);
  const toggleInverted = useHingesStore((s) => s.toggleInverted);

  const [collapsed, setCollapsed] = useState(() => defs.length > 12);
  const grouped = useMemo(() => groupByCategory(defs), [defs]);

  if (defs.length === 0) return null;

  return (
    <aside
      className={`hinges panel${collapsed ? ' is-collapsed' : ''}`}
      aria-label="Hinges"
    >
      <header className="hinges-header">
        <button
          type="button"
          className="hinges-collapse"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls="hinges-body"
          title={collapsed ? 'Expand hinges' : 'Collapse hinges'}
        >
          <span className="hinges-title">Hinges</span>
          <span className="hinges-count">{defs.length}</span>
          <span className="hinges-chevron" aria-hidden>
            {collapsed ? '▸' : '▾'}
          </span>
        </button>
        {!collapsed && (
          <div className="hinges-bulk">
            <button type="button" onClick={() => setAll(0)} title="Close every hinge">
              Close all
            </button>
            <button type="button" onClick={() => setAll(1)} title="Open every hinge">
              Open all
            </button>
          </div>
        )}
      </header>

      {!collapsed && (
        <div id="hinges-body" className="hinges-body">
          {CATEGORIES.map((cat) => {
            const items = grouped.get(cat.key) ?? [];
            if (items.length === 0) return null;
            return (
              <section key={cat.key} className="hinges-group">
                <h3>
                  {cat.title} <span className="hinges-group-count">({items.length})</span>
                </h3>
                <ul>
                  {items.map((def) => (
                    <HingeRow
                      key={def.id}
                      def={def}
                      value={values[def.id] ?? 0}
                      inverted={inverted[def.id] ?? false}
                      onChange={(v) => setValue(def.id, v)}
                      onToggleInvert={() => toggleInverted(def.id)}
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </aside>
  );
}

function HingeRow({
  def,
  value,
  inverted,
  onChange,
  onToggleInvert,
}: {
  def: HingeDef;
  value: number;
  inverted: boolean;
  onChange: (v: number) => void;
  onToggleInvert: () => void;
}) {
  const deg = hingeDegFromT(def, value) * (inverted ? -1 : 1);
  return (
    <li className="hinges-row">
      <div className="hinges-row-label" title={def.id}>
        <span>{def.label}</span>
        <span className="hinges-row-deg">{deg.toFixed(0)}°</span>
      </div>
      <div className="hinges-row-controls">
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          aria-label={`${def.label} hinge angle`}
        />
        <button
          type="button"
          className={`hinges-invert${inverted ? ' is-active' : ''}`}
          onClick={onToggleInvert}
          aria-pressed={inverted}
          title={inverted ? 'Restore default swing direction' : 'Invert swing direction'}
        >
          ⇆
        </button>
      </div>
    </li>
  );
}

function groupByCategory(defs: HingeDef[]): Map<HingeDef['category'], HingeDef[]> {
  const out = new Map<HingeDef['category'], HingeDef[]>();
  for (const cat of CATEGORIES) out.set(cat.key, []);
  for (const def of defs) {
    const list = out.get(def.category);
    if (list) list.push(def);
  }
  for (const list of out.values()) {
    list.sort((a, b) => a.label.localeCompare(b.label));
  }
  return out;
}
